import React, { createContext, useContext, useEffect, useState } from 'react';
import { translations } from './translations';

// Langues du site : le français est la langue d'origine, l'anglais la traduction.
export const LANGUAGES = ['fr', 'en'];
// Locale utilisée pour les dates et les nombres (toLocaleDateString...)
const LOCALES = { fr: 'fr-FR', en: 'en-GB' };
const STORAGE_KEY = 'site-pro-lang';

// Langue choisie par le visiteur (drapeau), sinon celle du navigateur :
// francophone -> français, toute autre langue -> anglais.
function detectLanguage() {
    try {
        const saved = window.localStorage.getItem(STORAGE_KEY);
        if (LANGUAGES.includes(saved)) return saved;
    }
    catch (error) {
        // stockage indisponible (navigation privée...) : on se rabat sur le navigateur
    }
    const browser = (navigator.languages && navigator.languages[0]) || navigator.language || 'fr';
    return browser.toLowerCase().startsWith('fr') ? 'fr' : 'en';
}

// Langue courante, lisible hors des composants (fonctions utilitaires de variables.js...).
// Les composants passent par useTranslation() pour être réaffichés au changement de langue.
let currentLang = detectLanguage();

export const getLang = () => currentLang;
export const getLocale = () => LOCALES[currentLang];

// Texte traduit d'un identifiant de translations.js, avec remplacement des {paramètres}.
// Identifiant inconnu : on l'affiche tel quel (repérable à l'écran) ;
// traduction anglaise manquante : on affiche le français.
export function t(id, params = {}) {
    const entry = translations[id];
    if (!entry) {
        if (process.env.NODE_ENV !== 'production') console.warn('Traduction manquante : ' + id);
        return id;
    }
    const text = entry[currentLang] ?? entry.fr;
    return text.replace(/\{(\w+)\}/g, (match, key) => (params[key] !== undefined ? params[key] : match));
}

const LanguageContext = createContext({ lang: currentLang, setLang: () => {} });

export function LanguageProvider({ children }) {
    const [lang, setLangState] = useState(currentLang);
    // mis à jour pendant le rendu : les enfants réaffichés lisent déjà la nouvelle langue
    currentLang = lang;

    useEffect(() => {
        document.documentElement.lang = lang;
    }, [lang]);

    const setLang = (value) => {
        if (!LANGUAGES.includes(value)) return;
        try {
            window.localStorage.setItem(STORAGE_KEY, value);
        }
        catch (error) {
            // choix non mémorisé, mais appliqué pour cette visite
        }
        setLangState(value);
    };

    return (
        <LanguageContext.Provider value={{ lang, setLang }}>
            {children}
        </LanguageContext.Provider>
    );
}

// À appeler dans chaque composant qui affiche du texte : abonne le composant au changement de langue.
export function useTranslation() {
    const { lang, setLang } = useContext(LanguageContext);
    return { t, lang, setLang, locale: LOCALES[lang] };
}
